import type { ServerModel, VerifyFitsValue } from 'Modules/serverApi/types';

import { AutocompleteType } from './autocompleteItem.types';
import FacetValue, { type FacetValueBase } from './facetValue';
import SearchItemData, { type SearchItemTemplateParams as SearchItemParams } from './searchItemData';

type TextValue = { text: string; textHighlight: string };
type AutocompleteFacetValue = FacetValueBase & {
  suggestionGroup: string;
  isRedirectFacetValue?: true;
  imageUrl?: string;
  redirectUrl?: string;
  payload?: string;
  facetValue?: string;
  productCategorySuggestion?: boolean;
};

export type AutocompleteItemParams = TextValue | SearchItemParams | AutocompleteFacetValue | {};

export default class AutocompleteItem {
  key: string;
  type: AutocompleteType;

  textValue?: TextValue;

  searchItem?: {
    data: SearchItemData;
    isRedirectItem?: true;
    redirectUrl?: string;
    suggestionGroup: string;
    fitsTheVehicle?: VerifyFitsValue;
  };

  facetValue?: AutocompleteFacetValue;

  constructor(item: ServerModel.AutocompleteItem) {
    switch (item.Type) {
      case 'Freetext':
      case 'PopularSearch':
        this.textValue = { text: item.Text, textHighlight: item.TextHighlight };
        this.type = AutocompleteType.Text;
        this.key = item.Text;
        break;
      case 'Product': {
        const data = new SearchItemData(item, 'autocomplete', 0);
        this.searchItem = {
          data,
          isRedirectItem: item.Redirect,
          redirectUrl: data.url,
          suggestionGroup: item.SuggestionGroup,
          fitsTheVehicle: item.fitsTheVehicle,
        };
        this.type = AutocompleteType.Product;
        this.key = data.key ?? '';
        break;
      }
      case 'Category': {
        this.facetValue = {
          field: item.FieldName ?? '',
          term: FacetValue.escapeTermValue(item.FacetValue),
          value: item.Value,
          facetValue: item.FacetValue,
          hitCount: toNumberOrUndefined(item.HitCount),
          suggestionGroup: item.SuggestionGroup,
          isRedirectFacetValue: item.Redirect,
          imageUrl: item.ImageUrl,
          payload: item.Payload,
          redirectUrl: item.RedirectUrl,
          productCategorySuggestion: item.ProductCategorySuggestion,
        };
        this.type = AutocompleteType.Facet;
        this.key = FacetValue.termKey(this.facetValue);
        break;
      }
    }
  }

  get redirectUrl(): string | undefined {
    return this.searchItem?.isRedirectItem
      ? this.searchItem.redirectUrl
      : this.facetValue?.isRedirectFacetValue
        ? this.facetValue.redirectUrl
        : undefined;
  }

  get suggestionGroup(): string | undefined {
    return this.facetValue?.suggestionGroup || this.searchItem?.suggestionGroup;
  }

  getParams(vehicleSelection: FacetValueBase[]): AutocompleteItemParams {
    return this.textValue || this.searchItem?.data.getParams(vehicleSelection) || this.facetValue || {};
  }
}

function toNumberOrUndefined(value: number | string | undefined): number | undefined {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? undefined : numericValue;
}
